import React from "react";
import { useParams } from "react-router-dom";
import DashboardLayout from "../../../Layout/DashboardLayout";
import Back from "../../../Components/Back";
import { useGetSingleDiscountQuery } from "../../../hooks/DiscountManagement/useGetSingleDiscountQuery";
import dayjs from "dayjs";
import Loader from "../../../Components/Loader";
import Header from "./Header";
import Scaffold from "Components/Scaffold";
import KoboConverter from "Components/KoboConverter";

const DiscountDetails = () => {
  const { id } = useParams();
  const {
    data: discount,
    isPending,
    isError,
  } = useGetSingleDiscountQuery(`${id}`);

  if (isPending) {
    return (
      <DashboardLayout>
        <div className="px-4 mt-5">
          <Back />
          <div className="mt-10 text-center">
            <Loader />
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (isError || !discount) {
    return (
      <DashboardLayout>
        <div className="px-4 mt-5">
          <Back />
          <div className="mt-10 text-center">
            <p className="text-red-500">
              Failed to load discount details. Please try again later.
            </p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Discount Details">
      <div className="px-4 mt-5">
        <Header />
        <div className="grid grid-cols-2 gap-6">
          <div className="space-y-4">
            <Scaffold left="General Details">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 px-3">
                <div>
                  <p className="text-gray-500">Discount Name:</p>
                  <p className="font-medium text-ash">
                    {discount.data.discountName || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Type:</p>
                  <p className="font-medium text-ash">
                    {discount.data.discountType || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Description:</p>
                  <p className="font-medium text-ash">
                    {discount.data.description || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Discount Code:</p>
                  <p className="font-medium text-ash">
                    {discount.data.discountCode || "N/A"}
                  </p>
                </div>
              </div>
            </Scaffold>
            <Scaffold left="Duration Details">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 px-4">
                <div>
                  <p className="text-gray-500">Start Date:</p>
                  <p className="font-medium text-ash">
                    {discount.data.startDate
                      ? dayjs(discount.startDate).format("MMM D, YYYY h:mm A")
                      : "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">End Date:</p>
                  <p className="font-medium text-ash">
                    {discount.data.endDate
                      ? dayjs(discount.endDate).format("MMM D, YYYY h:mm A")
                      : "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Percentage Off:</p>
                  <p className="font-medium text-ash">
                    {discount.data.percentageOff || 0}%
                  </p>
                </div>
              </div>
            </Scaffold>
            <Scaffold left="Created By">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 px-4">
                <div>
                  <p className="text-gray-500">Name:</p>
                  <p className="font-medium text-ash">
                    {discount.data.createdBy?.firstName || "N/A"}{" "}
                    {discount.data.createdBy?.lastName || ""}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Email:</p>
                  <p className="font-medium text-ash">
                    {discount.data.createdBy?.email || "N/A"}
                  </p>
                </div>
              </div>
            </Scaffold>
          </div>
          <div className="space-y-4">
            <Scaffold left="Amounts">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 px-4">
                <div>
                  <p className="text-gray-500">Minimum Order Amount:</p>
                  <p className="font-medium text-ash">
                    {discount.data.minimumOrderAmount ? (
                      <KoboConverter
                        amount={discount.data.minimumOrderAmount}
                      />
                    ) : (
                      "N/A"
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Max Discount Amount:</p>
                  <p className="font-medium text-ash">
                    {discount.data.maxDiscountAmount ? (
                      <KoboConverter amount={discount.data.maxDiscountAmount} />
                    ) : (
                      "N/A"
                    )}
                  </p>
                </div>
              </div>
            </Scaffold>
            <Scaffold left="Additional Details">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-gray-500">Times Used:</p>
                  <p className="font-medium text-ash">
                    {discount.data.timesUsed || 0}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Available:</p>
                  <p className="font-medium text-ash">
                    {discount.data.isAvailable ? (
                      <span className="text-green-600">Yes</span>
                    ) : (
                      <span className="text-red-600">No</span>
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Active:</p>
                  <p className="font-medium text-ash">
                    {discount.data.isActive ? (
                      <span className="text-green-600">Yes</span>
                    ) : (
                      <span className="text-red-600">No</span>
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Deleted:</p>
                  <p className="font-medium text-ash">
                    {discount.data.isDeleted ? (
                      <span className="text-red-600">Yes</span>
                    ) : (
                      <span className="text-green-600">No</span>
                    )}
                  </p>
                </div>
              </div>
            </Scaffold>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default DiscountDetails;
